import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AppConfigService } from './config/app.config.service';

export interface NextcloudArchivo {
  nombre: string;
  ruta: string;
  tipoDeContenido: string;
  tamano: number;
}

export interface NextcloudRecurso {
  fileid?: string;
  nombre: string;
  ubicacion: string;
  tipoDeContenido: string;
  esCarpeta: boolean;
  tamano: number;
}

export interface ArchivosConfig {
  raiz: string;
  carpetaPictogramas: string;
}

@Injectable({
  providedIn: 'root'
})
export class NextcloudService {

  private configCache: ArchivosConfig | null = null;
  private configRequest$: Observable<ArchivosConfig> | null = null;

  constructor(
    private http: HttpClient,
    private confServ: AppConfigService
  ) {}

  cargarConfigArchivos(): Observable<ArchivosConfig> {
    if (this.configCache) {
      return of(this.configCache);
    }

    if (!this.configRequest$) {
      const url = `${this.confServ.getConfig().apiUrlMtto}/v1/archivos/config`;
      this.configRequest$ = this.http.get<ArchivosConfig>(url).pipe(
        tap((config) => {
          this.configCache = config;
        }),
        shareReplay(1),
        catchError((err) => {
          this.configRequest$ = null;
          throw err;
        })
      );
    }

    return this.configRequest$;
  }

  listarImagenesPictogramas(): Observable<NextcloudArchivo[]> {
    const url = `${this.confServ.getConfig().apiUrlMtto}/v1/archivos/pictogramas`;
    return this.http.get<NextcloudArchivo[]>(url);
  }

  listarArchivos(carpeta: string): Observable<NextcloudRecurso[]> {
    const url = `${this.confServ.getConfig().apiUrlMtto}/v1/archivos/listar`;
    const params = new HttpParams().set('carpeta', (carpeta || '').trim());
    return this.http.get<NextcloudRecurso[]>(url, { params });
  }

  cargarArchivo(carpeta: string, archivo: File): Observable<any> {
    const url = `${this.confServ.getConfig().apiUrlMtto}/v1/archivos/cargar`;
    const formData = new FormData();
    formData.append('carpeta', (carpeta || '').trim());
    formData.append('nombre-de-archivo', archivo.name);
    formData.append('archivo', archivo, archivo.name);
    return this.http.put(url, formData);
  }

  eliminarArchivo(ruta: string): Observable<void> {
    const url = `${this.confServ.getConfig().apiUrlMtto}/v1/archivos/eliminar`;
    const params = new HttpParams().set('ruta', (ruta || '').trim());
    return this.http.delete<void>(url, { params });
  }

  getCarpetaPictogramas(): string {
    return this.configCache?.carpetaPictogramas || '';
  }

  getImagenUrl(ruta: string): string {
    const rutaLimpia = (ruta || '').trim();
    return `${this.confServ.getConfig().apiUrlMtto}/v1/archivos/imagen?ruta=${encodeURIComponent(rutaLimpia)}`;
  }

  /**
   * Descarga la imagen vía HttpClient (lleva Bearer token automáticamente
   * por el interceptor) y la devuelve como data URL base64.
   * Usar esto en lugar de <img [src]> directo para rutas de Nextcloud.
   */
  getImagenComoBase64(urlImage: string): Observable<string> {
    const proxyUrl = this.getImagenUrl(urlImage);
    return this.http.get(proxyUrl, { responseType: 'blob', observe: 'response' }).pipe(
      switchMap(response => {
        const blob = response.body;
        if (!blob || blob.size === 0) {
          return of('');
        }

        const headerType = response.headers.get('Content-Type')?.split(';')[0].trim();
        const tipo = this.resolverTipoImagen(headerType, urlImage, blob.type);
        const blobConTipo = blob.type === tipo ? blob : new Blob([blob], { type: tipo });

        return new Observable<string>(observer => {
          const reader = new FileReader();
          reader.readAsDataURL(blobConTipo);
          reader.onloadend = () => {
            observer.next(reader.result as string);
            observer.complete();
          };
          reader.onerror = () => {
            observer.next('');
            observer.complete();
          };
        });
      }),
      catchError((err) => {
        console.error('Error al obtener imagen de Nextcloud:', err);
        return of('');
      })
    );
  }

  private resolverTipoImagen(headerType: string | undefined, ruta: string, blobType: string): string {
    if (headerType && headerType.startsWith('image/')) {
      return headerType;
    }
    if (blobType && blobType.startsWith('image/')) {
      return blobType;
    }

    const nombre = (ruta || '').split('/').pop() || '';
    const extension = nombre.includes('.') ? nombre.split('.').pop()?.toLowerCase() : '';
    switch (extension) {
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'svg':
        return 'image/svg+xml';
      default:
        return headerType || blobType || 'image/png';
    }
  }
}
